import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Mail,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  RefreshCw,
  Shield,
  Clock,
  ArrowRight,
  Home,
  LogIn,
} from "lucide-react";
import { useDispatch } from "react-redux";
import { userService } from "../api/services/userService";
import { useToast } from "../components/common/ThemeToaster";
import { markEmailVerified } from "../store/slices/authSlice";

const Verify = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const { success: toastSuccess, error: toastError } = useToast();

  const [status, setStatus] = useState("verifying");
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [isResending, setIsResending] = useState(false);

  useEffect(() => {
    if (token) {
      verifyEmail(token);
    } else {
      setStatus("error");
      setErrorMessage(
        "No verification token found. Please check your email link.",
      );
      setLoading(false);
    }
  }, [token]);

  const verifyEmail = async (verificationToken) => {
    try {
      setLoading(true);
      setStatus("verifying");

      const response =
        await userService.verifyEmailWithToken(verificationToken);

      if (response?.status === "success") {
        setStatus("success");
        dispatch(markEmailVerified());
        toastSuccess("Email verified successfully!");

        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate("/user/login", {
            state: { message: "Email verified! You can now log in." },
          });
        }, 3000);
      } else {
        setStatus("error");
        setErrorMessage(response?.message || "Failed to verify email.");
        toastError(response?.message || "Failed to verify email.");
      }
    } catch (err) {
      console.error("Email verification error:", err);
      setStatus("error");
      setErrorMessage(
        err?.response?.data?.message ||
          err?.message ||
          "Invalid or expired verification link. Please request a new one.",
      );
      toastError("Verification failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setIsResending(true);
    try {
      await userService.resendVerificationEmail();
      toastSuccess("Verification email sent! Check your inbox.");
    } catch (err) {
      toastError(
        err?.response?.data?.message || "Failed to resend verification email.",
      );
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-[40%] -left-[20%] w-[80%] h-[80%] bg-gradient-to-br from-primary-200/20 to-secondary-200/20 dark:from-primary-900/20 dark:to-secondary-900/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute -bottom-[40%] -right-[20%] w-[80%] h-[80%] bg-gradient-to-tl from-primary-300/10 to-secondary-300/10 dark:from-primary-800/10 dark:to-secondary-800/10 rounded-full blur-3xl animate-pulse delay-1000" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full relative z-10"
      >
        <div className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-200/50 dark:border-gray-800/50 overflow-hidden">
          {/* Header */}
          <div className="p-6 sm:p-8 bg-gradient-to-r from-primary-500/10 to-secondary-500/10 border-b border-gray-100 dark:border-gray-800 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 mb-4 shadow-lg">
              <Mail size={32} className="text-white" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Email Verification
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {status === "verifying" && "Verifying your email address..."}
              {status === "success" && "Your email has been verified!"}
              {status === "error" && "Verification failed"}
            </p>
          </div>

          {/* Content */}
          <div className="p-6 sm:p-8">
            <AnimatePresence mode="wait">
              {/* Verifying State */}
              {status === "verifying" && (
                <motion.div
                  key="verifying"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="text-center py-8"
                >
                  <div className="relative w-16 h-16 mx-auto mb-6">
                    <div className="absolute inset-0 border-4 border-primary-200 dark:border-primary-800 rounded-full" />
                    <div className="absolute inset-0 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Mail size={24} className="text-primary-500" />
                    </div>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400">
                    Please wait while we verify your email address...
                  </p>
                </motion.div>
              )}

              {/* Success State */}
              {status === "success" && (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  className="text-center py-8"
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 200, damping: 15 }}
                    className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg"
                  >
                    <CheckCircle size={40} className="text-white" />
                  </motion.div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                    Verification Successful!
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">
                    Your email address has been verified. You can now access all
                    features of your account.
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 flex items-center justify-center gap-2">
                    <Clock size={14} />
                    Redirecting to login in a few seconds...
                  </p>
                  <Link
                    to="/user/login"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 text-white font-semibold rounded-xl hover:shadow-lg transition-all"
                  >
                    Go to Login
                    <ArrowRight size={18} />
                  </Link>
                </motion.div>
              )}

              {/* Error State */}
              {status === "error" && (
                <motion.div
                  key="error"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  className="text-center py-8"
                >
                  <div className="w-20 h-20 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <XCircle size={40} className="text-red-500" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                    Verification Failed
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">
                    {errorMessage ||
                      "The verification link is invalid or has expired."}
                  </p>

                  <div className="space-y-3">
                    <button
                      onClick={handleResendVerification}
                      disabled={isResending}
                      className="w-full py-3 bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-semibold rounded-xl hover:shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {isResending ? (
                        <Loader2 size={18} className="animate-spin" />
                      ) : (
                        <RefreshCw size={18} />
                      )}
                      {isResending ? "Sending..." : "Resend Verification Email"}
                    </button>

                    <div className="flex gap-3">
                      <Link
                        to="/user/login"
                        className="flex-1 py-2.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 font-medium rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700 transition-all flex items-center justify-center gap-2"
                      >
                        <LogIn size={16} />
                        Login
                      </Link>
                      <Link
                        to="/"
                        className="flex-1 py-2.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 font-medium rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700 transition-all flex items-center justify-center gap-2"
                      >
                        <Home size={16} />
                        Home
                      </Link>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Footer */}
          <div className="p-4 bg-gray-50/50 dark:bg-gray-800/30 border-t border-gray-100 dark:border-gray-800 text-center">
            <div className="flex items-center justify-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              <Shield size={12} />
              <span>
                Your email is protected with enterprise-grade security
              </span>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Verify;
